import React, { useContext } from 'react';
import moment from "moment";
import { AppContext } from '../../../context/app-context';
import { AdminBookingReportVO } from "../../../types/model/valueObject/booking/AdminBookingReportVO";
import { formatCurrencyValue } from "../../../util/PaymentUtils";
import { formatEndDateDisplay, formatBookingTimestamp, getBrowserTimezone } from "../../../util/DateUtils";
import { adminBookingsReportEndpoints } from "../../../requests/api-endpoints";
import { dialogs } from "./AdminBookingDialogWrapper";
import { ReportDialogSetterProps } from "../../../interfaces";
import { hasSuperAdminAccess } from "../../../util/AccountUtils";
import {
  BookingDurationType,
  BookingStatus,
  BookingSubscriptionStatus,
  BookingReasonDeclinedLabel
} from "../../constants/securspace-constants";
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardBodyActionsContainer,
  ActionsMenuOpenButton,
  ActionsMenu,
  useMenu
} from "@securspace/securspace-ui-kit";
import {
  ActionItem,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem
} from "../../cardComponents";
import {
  ActionTypes,
  ActionDefaultIcons,
  executeDownload
} from "../../../util/ActionUtils";

type AdminBookingCardListItemProps = {
  item: AdminBookingReportVO;
  expanded?: boolean;
  onToggleExpand: () => void;
  onOpenDialog: (dialog: ReportDialogSetterProps<AdminBookingReportVO>) => void;
}

const downloadActionPayload = {
  endpoint: (item: AdminBookingReportVO) =>
              adminBookingsReportEndpoints.actions.downloadBookingHistory(item.id),
  errorMessage: "Unable to download history for this booking",
};

/**
 * Component that displays all information for a single booking
 * @param item the AdminBookingReportVO to display
 * @param expanded
 * @param onToggleExpand
 * @param onOpenDialog
 * @constructor
 */
const AdminBookingCardListItem = ({
  item,
  expanded,
  onToggleExpand,
  onOpenDialog
}: AdminBookingCardListItemProps) => {
  const { user } = useContext(AppContext);
  const timezone = getBrowserTimezone();
  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();

  const handleAction = (dialog: string) => {
    handleClose();
    onOpenDialog({ item, dialog })
  }

  return (
    <CollapsableCardContainer expanded={expanded}>
        <CollapsableCardClickableContainer onClick={onToggleExpand}>
          <CollapsableCardHeaderContainer>
            <CollapsableCardHeaderItem value={item?.orderNumber} label={'Booking Number'} />
            <CollapsableCardHeaderItem value={item?.supplierCompanyName} label={'Supplier'} />
            <CollapsableCardHeaderItem value={item?.buyerCompanyName} label={'Buyer'} />
            <CollapsableCardHeaderItem value={`${item?.startDate} ${formatEndDateDisplay(item?.endDate, 'MM/DD/YYYY', true)}`} label={'Booked Dates'} />
            <CollapsableCardHeaderItem value={item?.numberOfSpaces} label={'Spaces Booked'} />
            <CollapsableCardHeaderItem value={item?.durationType} label={'Frequency'} />
            <CollapsableCardHeaderActionsContainer>
              <CollapsableCardExpandIconButton expanded={expanded ?? false} />
            </CollapsableCardHeaderActionsContainer>
          </CollapsableCardHeaderContainer>
        </CollapsableCardClickableContainer>
        <CollapsableCardCollapseContainer in={expanded}>
          <CollapsableCardBodyContainer>
            <CollapsableCardBodyItem value={item?.locationName} label={'Location'} />
            <CollapsableCardBodyItem value={item?.locationAddress} label={'Address'} />
            <CollapsableCardBodyItem value={item?.locationState} label={'State'} />
            <CollapsableCardBodyItem value={item?.brokered} label={'Brokered'} />
            <CollapsableCardBodyItem value={item?.endDateAdjusted} label={'Extended/Date Adjusted'} />
            <CollapsableCardBodyItem value={formatCurrencyValue(item?.buyerRatePerSpace, true)} label={'Rate Per Space'} />
            <CollapsableCardBodyItem value={formatCurrencyValue(item?.initialCharge, true)} label={'Total Customer Amount'} />
            <CollapsableCardBodyItem value={formatCurrencyValue(item?.initialPaymentAmount, true)} label={'Initial Payment Amount'} />
            <CollapsableCardBodyItem value={formatCurrencyValue(item?.initialSecurSpaceFeeAmount, true)} label={'Initial Securspace Amount'} />
            <CollapsableCardBodyItem value={formatCurrencyValue(item?.initialPaymentProcessorFee, true)} label={'Initial Payment Processor Fee'} />
            <CollapsableCardBodyItem value={formatCurrencyValue(item?.initialSupplierPayoutAmount, true)} label={'Initial Partner Payout Amount'} />
            <CollapsableCardBodyItem value={item?.securSpacePercentage} label={'Securspace Percentage'} />
            <CollapsableCardBodyItem value={item?.gmsYard} label={'GMS Yard'} />
            <CollapsableCardBodyItem value={item?.bookingSubscriptionStatus} label={'Subscription Status'} />
            <CollapsableCardBodyItem value={formatBookingTimestamp(item?.creationDate, timezone)} label="Created On" />
            <CollapsableCardBodyItem value={item?.buyerEmail} label={'Buyer Email Address'} />
            <CollapsableCardBodyItem value={item?.chargeOverages} label={'Charge Overages'} />
            <CollapsableCardBodyItem value={item?.overageRate} label={'Overage Rate'} />
            <CollapsableCardBodyItem value={item?.assetType} label={'Equipment Type'} />
            <CollapsableCardBodyItem value={item?.status} label={'Status'} />
            <CollapsableCardBodyItem value={item?.reasonDeclined} label={'Reason Declined'} />
            {
              item?.status === BookingStatus.DECLINED &&
              item?.reasonDeclined !== BookingReasonDeclinedLabel.AUTO_DECLINED &&
              <CollapsableCardBodyItem value={item?.reasonDeclinedNote} label={'Reason Declined Note'} />
            }

            <CollapsableCardBodyActionsContainer>
              <ActionsMenuOpenButton onClick={handleClick} />
              {/* @ts-expect-error open is a valid prop. Need to edit uikit to indicate this. */}
              <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
                <ActionItem
                  label="Adjust End Date"
                  icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                  onClick={() => handleAction(dialogs.ADJUST_END_DATE)}
                  show={item?.durationType?.toUpperCase() !== BookingDurationType.DAILY.toUpperCase()}
                />
                <ActionItem
                  label="Cancel Booking"
                  icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                  onClick={() => handleAction(dialogs.CANCEL_BOOKING)}
                  show={
                    item?.status !== BookingStatus.CANCELLED && (
                      moment(item?.startDate).isAfter(moment().add(1, "days")) ||
                      hasSuperAdminAccess(user)
                    )
                  }
                />
                <ActionItem
                  label="Toggle Charge Overages"
                  icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                  onClick={() => handleAction(dialogs.TOGGLE_CHARGE_OVERAGES)}
                  show={Boolean(item?.id)}
                />
                <ActionItem
                  label="Set to Manual Payment"
                  icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                  onClick={() => handleAction(dialogs.SET_TO_MANUAL_PAYMENT)}
                  show={item?.status !== BookingStatus.MANUAL_PAYMENT}
                />
                <ActionItem
                  label="End Booking"
                  icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                  onClick={() => handleAction(dialogs.END_BOOKING)}
                  show={
                    Boolean(item?.durationType) &&
                    item.durationType.toUpperCase() !== BookingDurationType.DAILY.toUpperCase() &&
                    moment(item?.endDate).isAfter(moment()) &&
                    item?.bookingSubscriptionStatus?.toUpperCase() !== BookingSubscriptionStatus.CANCELLED.toUpperCase()
                  }
                />
                <ActionItem
                  label="Create Next Invoice"
                  icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                  onClick={() => handleAction(dialogs.CREATE_NEXT_INVOICE)}
                  show={
                    item?.status === BookingStatus.MANUAL_PAYMENT &&
                    Boolean(item?.durationType) &&
                    item.durationType.toUpperCase() !== BookingDurationType.DAILY.toUpperCase()
                  }
                />
                <ActionItem
                  label="Download Booking History"
                  icon={ActionDefaultIcons[ActionTypes.DOWNLOAD]}
                  onClick={() => executeDownload(item, downloadActionPayload, handleClose)}
                  enable={Boolean(item?.id)}
                />
              </ActionsMenu>
            </CollapsableCardBodyActionsContainer>
          </CollapsableCardBodyContainer>
        </CollapsableCardCollapseContainer>
      </CollapsableCardContainer>
  );
};

export { AdminBookingCardListItem };
